import { PropsWithChildren, useEffect, FunctionComponent } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from 'src/config/routes';
import PageLayout from 'src/layouts/PageLayout';
import LessonFlowHeader from 'src/components/AddLesson/LessonFlowHeader';
import {
  LESSON_FLOW_STEP_INDEX,
  LessonFlowStep,
  lessonFlowBackPath,
  useLessonFlowTotal,
} from 'src/utils/lessonFlowSteps';
import { logAnalyticsEvent } from 'src/modules/analytics/analytics';
import { useLessonFlowHeaderOverrides } from './utils/lessonFlowHeaderStore';
import { useUserRole } from 'src/hooks/useUserRole';

const FLOW_ROUTES: { path: string; step: LessonFlowStep; closes?: true }[] = [
  { path: ROUTES.LESSONS_SELECT_SUBJECT.path, step: 'subject' },
  { path: ROUTES.SUBJECT_SELECT_MEDIA.path, step: 'photos' },
  { path: ROUTES.LESSON_EVALUATION_ASK.path, step: 'evaluation', closes: true },
  { path: ROUTES.LESSON_EVALUATION_DATE.path, step: 'evaluationDate' },
  { path: ROUTES.LESSON_NOTIFICATIONS.path, step: 'notifications' },
  { path: ROUTES.LESSON_PROCESSING.path, step: 'processing', closes: true },
];

/** `search` matters: `select-media` is also the add-photos route of an existing lesson. */
export const matchLessonFlowRoute = (pathname: string, search = '') => {
  for (const route of FLOW_ROUTES) {
    const match = matchPath(route.path, pathname);
    if (!match) continue;
    if (route.step === 'photos' && new URLSearchParams(search).has('addContent')) {
      return null;
    }
    return { ...route, lessonId: match.params.lessonId };
  }
  return null;
};

const LessonFlowContainer: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { pathname, search, state } = useLocation();
  const navigate = useNavigate();
  const total = useLessonFlowTotal();
  const { isParent } = useUserRole();
  const { fraction, onBack } = useLessonFlowHeaderOverrides();
  const home =
    isParent
      ? ROUTES.PARENT_HOME.path
      : ROUTES.CHILD_HOME.path;

  const current = matchLessonFlowRoute(pathname, search);
  const step = current?.step;

  const backPath = current
    ? lessonFlowBackPath({
        lessonId: current.lessonId,
        step: current.step,
        hasEvaluationDate: (state as { hasEvaluationDate?: boolean } | null)
          ?.hasEvaluationDate,
      })
    : undefined;
  const goBack = backPath
    ? () => navigate(backPath, { replace: true, state: { flowTotal: total } })
    : undefined;

  useEffect(() => {
    if (!step) return;
    void logAnalyticsEvent('lesson_flow_step_viewed', {
      step_kind: step,
      step_index: LESSON_FLOW_STEP_INDEX[step],
    });
  }, [step]);

  return (
    // The flow has its own route tree, so mounting plays the slide that navigation would not.
    <div className="h-full w-full animate-in slide-in-from-right-full duration-md ease-out">
      <PageLayout fitContent>
        {current ? (
          <LessonFlowHeader
            step={current.step}
            total={total}
            fraction={fraction}
            onBack={onBack ?? goBack}
            onClose={
              current.closes
                ? () => navigate(home, { replace: true })
                : undefined
            }
          />
        ) : null}
        <div className="flex min-h-0 flex-1 flex-col [&>.slide-routes]:min-h-0 [&>.slide-routes]:flex-1">
          {children}
        </div>
      </PageLayout>
    </div>
  );
};

export default LessonFlowContainer;
